
//===========================================================================================
// 28Th Oct, 2025 src/components/tenantcomponents/TenantRegistration/TenantRegistrationForm.js
//===========================================================================================
import React, { useState } from 'react';
import {
  Box, Stepper, Step, StepLabel, Button, Typography,
  TextField, FormControlLabel, Checkbox, Alert,
  Container, Paper, CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../../services/api';
import TenantMSA from '../LegalAgreements/TenantMSA';
import AcceptableUsePolicy from '../LegalAgreements/AcceptableUsePolicy';
import PrivacyPolicy from '../LegalAgreements/PrivacyPolicy';

const steps = ['Pharmacy Details', 'Legal Agreements', 'Confirmation'];

const TenantRegistrationForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    // Subdomain & Basic Info
    desired_subdomain: '',
    firm_name: '',
    contact_person: '',
    email: '',
    phone: '',
    
    // Business Details
    address: '',
    license_number: '',
    gstin: '',
    
    // Legal Agreements
    accepted_msa: false,
    accepted_aup: false,
    accepted_privacy: false
  });

  // Subdomain validation
  const validateSubdomain = (value) => {
    if (!value) return 'Subdomain is required';
    if (!/^[a-z0-9-]+$/.test(value)) {
      return 'Only lowercase letters, numbers, and hyphens allowed';
    }
    if (value.length < 3) {
      return 'Subdomain must be at least 3 characters';
    }
    if (value.startsWith('-') || value.endsWith('-')) {
      return 'Subdomain cannot start or end with hyphen';
    }
    return '';
  };

  const handleNext = () => {
    setError('');
    
    // Validate current step
    if (activeStep === 0) {
      const subdomainError = validateSubdomain(formData.desired_subdomain);
      if (subdomainError) {
        setError(subdomainError);
        return;
      }
      if (!formData.firm_name || !formData.contact_person || !formData.email || !formData.phone) {
        setError('All fields are required');
        return;
      }
    }
    
    if (activeStep === 1) {
      if (!formData.accepted_msa || !formData.accepted_aup || !formData.accepted_privacy) {
        setError('All agreements must be accepted to continue');
        return;
      }
    }
    
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/tenant-registration/', formData);
      
      if (response.status === 201) {
        setSuccess('Registration submitted successfully! We will review and notify you via email.');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 
                       err.response?.data?.detail || 
                       'Registration failed. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pharmacy Information
            </Typography>
            
            <TextField
              label="Your Subdomain"
              placeholder="e.g., sheikh-pharmacy"
              value={formData.desired_subdomain}
              onChange={(e) => {
                const value = e.target.value.toLowerCase();
                setFormData({...formData, desired_subdomain: value});
              }}
              error={!!validateSubdomain(formData.desired_subdomain)}
              helperText={
                validateSubdomain(formData.desired_subdomain) || 
                `Your URL: ${formData.desired_subdomain || 'example'}.medicerp.in`
              }
              required
              fullWidth
            />
            
            <TextField
              label="Pharmacy/Firm Name"
              value={formData.firm_name}
              onChange={(e) => setFormData({...formData, firm_name: e.target.value})}
              required
              fullWidth
            />
            
            <TextField
              label="Contact Person Name"
              value={formData.contact_person}
              onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
              required
              fullWidth
            />
            
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              fullWidth
            />
            
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              required
              fullWidth
            />
            
            <TextField
              label="Business Address"
              multiline
              rows={3}
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              required
              fullWidth
            />
            
            <TextField
              label="Drug License Number"
              value={formData.license_number}
              onChange={(e) => setFormData({...formData, license_number: e.target.value})}
              required
              fullWidth
            />
            
            <TextField
              label="GSTIN"
              value={formData.gstin}
              onChange={(e) => setFormData({...formData, gstin: e.target.value})}
              required
              fullWidth
            />
          </Box>
        );
      
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>
              Legal Agreements
            </Typography>
            
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Master Services Agreement
              </Typography>
              <TenantMSA />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.accepted_msa}
                    onChange={(e) => setFormData({...formData, accepted_msa: e.target.checked})}
                  />
                }
                label="I have read and agree to the Master Services Agreement"
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Acceptable Use Policy
              </Typography>
              <AcceptableUsePolicy />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.accepted_aup}
                    onChange={(e) => setFormData({...formData, accepted_aup: e.target.checked})}
                  />
                }
                label="I accept the Acceptable Use Policy"
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Privacy Policy
              </Typography>
              <PrivacyPolicy />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.accepted_privacy}
                    onChange={(e) => setFormData({...formData, accepted_privacy: e.target.checked})}
                  />
                }
                label="I accept the Privacy Policy and data processing terms"
              />
            </Box>
          </Box>
        );
      
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Registration Summary
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography><strong>Subdomain:</strong> {formData.desired_subdomain}.medicerp.in</Typography>
              <Typography><strong>Pharmacy:</strong> {formData.firm_name}</Typography>
              <Typography><strong>Contact:</strong> {formData.contact_person}</Typography>
              <Typography><strong>Email:</strong> {formData.email}</Typography>
              <Typography><strong>Phone:</strong> {formData.phone}</Typography>
              <Typography><strong>License:</strong> {formData.license_number}</Typography>
              <Typography><strong>GSTIN:</strong> {formData.gstin}</Typography>
            </Box>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> After admin approval, you will receive an email notification. 
                Your staff can then register on your subdomain and our team will approve their accounts.
              </Typography>
            </Alert>
            
            <Alert severity="warning">
              <Typography variant="body2">
                By submitting, you confirm all information is accurate and you accept all legal agreements.
              </Typography>
            </Alert>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Pharmacy Registration
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4, textAlign: 'center' }}>
          Register your pharmacy to get started with MedicERP
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0 || loading}
          >
            Back
          </Button>
          
          <Box sx={{ flex: 1 }} />
          
          {activeStep === steps.length - 1 ? (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Submitting...' : 'Complete Registration'}
            </Button>
          ) : (
            <Button onClick={handleNext} variant="contained">
              Next
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default TenantRegistrationForm;